// DetailSheet — 1:1 port of legacy `src/ui/DetailSheet.jsx`.
// Header row (back-button → onClose, title, optional right slot, optional
// actions slot) + scrollable body containing `children`.
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon.js';

export interface DetailSheetProps {
  title: string;
  right?: ReactNode;
  actions?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /**
   * When true, the header back button is disabled and click on it is ignored.
   * Mirrors `Modal.dismissDisabled` / `Drawer.dismissDisabled` so unsaved-changes
   * guards can suppress dismissal while a warning prompt is open or a mutation
   * is in flight.
   */
  dismissDisabled?: boolean;
}

export function DetailSheet({
  title,
  right,
  actions,
  onClose,
  children,
  dismissDisabled = false,
}: DetailSheetProps) {
  const { t } = useTranslation('common');
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'var(--bg-0)',
        overflow: 'hidden',
      }}
    >
      <header
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          padding: '10px 16px',
          borderBottom: '1px solid var(--border-1)',
          background: 'var(--bg-1)',
        }}
      >
        <button
          onClick={() => {
            if (dismissDisabled) return;
            onClose();
          }}
          aria-label={t('actions.back')}
          disabled={dismissDisabled}
          style={{
            width: 32,
            height: 32,
            flexShrink: 0,
            border: 'none',
            borderRadius: 'var(--r-1)',
            background: 'transparent',
            cursor: dismissDisabled ? 'not-allowed' : 'pointer',
            opacity: dismissDisabled ? 0.45 : 1,
            color: 'var(--fg-2)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="arrow-left" size={16} />
        </button>

        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            flex: '1 1 0',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'var(--fg-1)',
          }}
        >
          {title}
        </div>

        {right && <div style={{ flexShrink: 0 }}>{right}</div>}

        {actions && <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>{actions}</div>}
      </header>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '16px',
        }}
      >
        {children}
      </div>
    </div>
  );
}
